import { useEffect } from "react";
import { useFormContext } from "@jsq/ds/forms";
import { isUSA } from "../../../../../common/utils";
import { getNameFromCountry } from "../../../mappers";
import { BankDetailsInputFields, type BankDetailsInputType } from "./constants";

/**
 * This hook will reset the following details:
 *
 * When the bank country changes, the routing number / swift code + intl routing number is reset
 * When the intermediary bank country changes, the intermediary routing number / intermediary swift code + intl routing number is reset
 * When the option to include an intermediary bank country is deselected, then all the values related to intermediary bank is reset
 *
 * Note: bank name and address are reset automatically from within the routing number / swift components
 *
 */
export const useResetBankDetailsOnFormChange = () => {
  const { values, setFieldValue, setValues } = useFormContext<BankDetailsInputType>();

  /**
   * - When the bank country changes and it is changed to US then reset SWIFT/International Routing fields
   * - When the bank country changes and it is changed to non-US then reset US specific Routing fields
   */
  useEffect(() => {
    if (isUSA(getNameFromCountry(values.bankCountry))) {
      void setValues({
        ...values,
        [BankDetailsInputFields.SwiftCode]: "",
        [BankDetailsInputFields.InternationalRoutingNumber]: "",
        /**
         * This will be re-established upon re-validating routing/swift input.
         */
        [BankDetailsInputFields.BankCountryValidatedCountry]: "",
      });
    } else {
      void setFieldValue(BankDetailsInputFields.RoutingNumber, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFieldValue, setValues, values.bankCountry]);

  /**
   * - When the intermediary bank country changes and it is changed to US then reset SWIFT/International Routing fields
   * - When the intermediary bank country changes and it is changed to non-US then reset US specific Routing fields
   */
  useEffect(() => {
    if (isUSA(getNameFromCountry(values.intermediaryBankCountry))) {
      void setValues({
        ...values,
        [BankDetailsInputFields.IntermediarySwiftCode]: "",
        [BankDetailsInputFields.IntermediaryInternationalRoutingNumber]: "",
        /**
         * This will be re-established upon re-validating routing/swift input.
         */
        [BankDetailsInputFields.IntermediaryBankCountryValidatedCountry]: "",
      });
    } else {
      void setFieldValue(BankDetailsInputFields.IntermediaryRoutingNumber, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFieldValue, setValues, values.intermediaryBankCountry]);

  /**
   * When intermediary usage is deselected then clear all routing specific fields and validated bank country field.
   */
  useEffect(() => {
    if (!values.usesIntermediaryBank) {
      void setValues({
        ...values,
        [BankDetailsInputFields.IntermediaryBankCountry]: "",
        [BankDetailsInputFields.IntermediaryRoutingNumber]: "",
        [BankDetailsInputFields.IntermediarySwiftCode]: "",
        [BankDetailsInputFields.IntermediaryInternationalRoutingNumber]: "",
        /**
         * This will be re-established upon re-validating routing/swift input.
         */
        [BankDetailsInputFields.IntermediaryBankCountryValidatedCountry]: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValues, values.usesIntermediaryBank]);
};

/**
 * Validates that the provided country matches the validated country.
 *
 * @param countryValue - The raw country value from the input.
 * @param validatedCountry - The validated country name.
 * @returns {boolean} - True if the countries match or if either value is missing, otherwise false.
 */
export const validateBankCountryMatch = (
  countryValue: string | undefined,
  validatedCountry: string | undefined,
): boolean => {
  if (!countryValue || !validatedCountry) {
    // If either value is not provided, skip this validation
    return true;
  }
  const nameFromCountry = getNameFromCountry(countryValue);
  return nameFromCountry?.toLowerCase() === validatedCountry.toLowerCase();
};

/**
 * Validates that the provided intermediary bank country matches the validated country.
 *
 * @param usesIntermediaryBank - Indicates if an intermediary bank is used.
 * @param countryValue - The raw intermediary bank country value from the input.
 * @param validatedCountry - The validated intermediary bank country name.
 * @returns {boolean} - True if the countries match, intermediary bank is not used, or if either value is missing. Otherwise, false.
 */
export const validateIntermediaryBankCountryMatch = (
  usesIntermediaryBank: boolean,
  countryValue: string | undefined,
  validatedCountry: string | undefined,
): boolean => {
  if (!usesIntermediaryBank) {
    // If intermediary bank is not used, skip this validation
    return true;
  }
  if (!countryValue || !validatedCountry) {
    // If either value is not provided, skip this validation
    return true;
  }
  const nameFromCountry = getNameFromCountry(countryValue);
  return nameFromCountry?.toLowerCase() === validatedCountry.toLowerCase();
};
